import { useFieldArray, useFormContext } from "react-hook-form";
import { ContentContainer } from "../../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../../components/content-section/ContentSection";
import { StepMenuStepProps } from "../../../../components/step-menu/StepMenu";
import { TagGroupFieldKeyType, ObjectTagValuesConfigurationFieldType, TagGroupTagsFieldArrayType } from "../ObjectTagValuesConfigurationFieldTypes";
import TabBar from "../../../tab-bar/TabBar";
import TabWithCount from "../../../tab-with-count/TabWithCount";
import TabContainer from "../../../tab-container/TabContainer";
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Box } from "@mui/material";
import DisplayOptionsCard from "./DisplayOptionsCard";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import ListDragOverlay from "../../../list-drag-overlay/ListDragOverlay";

export type DisplayOptionsStepProps = {
    tagGroupKey: TagGroupFieldKeyType;
} & StepMenuStepProps;

export default function DisplayOptionsStep({
    tagGroupKey
}: DisplayOptionsStepProps) {

    const {
        watch
    } = useFormContext<ObjectTagValuesConfigurationFieldType>();

    const {
        fields,
        move
    } = useFieldArray<ObjectTagValuesConfigurationFieldType, `${TagGroupFieldKeyType}.tags`, "fieldID">({
        name: `${tagGroupKey}.tags`,
        keyName: "fieldID"
    });

    // TODO: Don't think I really need this because I don't need to reflect changes to the items themseleves. just the order.
    const watchTags = watch(`${tagGroupKey}.tags`);

    const tags: TagGroupTagsFieldArrayType = fields.map((field, index) => {
        return {
            ...field,
            ...watchTags[index],
            tagKey: `${tagGroupKey}.tags.${index}`
        };
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 0,//isMobile ? 500 : 0,
                tolerance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = tags.findIndex(t => t.equatableKey === active.id);
            const newIndex = tags.findIndex(t => t.equatableKey === over?.id);

            if (oldIndex !== null && newIndex !== null) {
                move(oldIndex, newIndex);
            }
        }
    }

    return (
        <>
            <TabBar value={0}>
                <TabWithCount label="Tag Values" />
            </TabBar>
            <TabContainer visible={true}>
                <ContentContainer>
                    <ContentSection>

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    >
                        <SortableContext
                            items={tags.map(tag => tag.equatableKey)}
                            strategy={verticalListSortingStrategy}
                        >
                            <Box sx={{ overflowY: "auto" }}>
                                {tags.map(tag => {
                                    return (
                                        <DisplayOptionsCard
                                            key={tag.fieldID}
                                            tagKey={tag.tagKey}
                                        />
                                    );
                                })}
                            </Box>
                            <ListDragOverlay />
                        </SortableContext>
                    </DndContext>

                    </ContentSection>
                </ContentContainer>
            </TabContainer>
        </>
    );
}